/**
 * The inline HSV picker: a saturation/value plane under a hue bar, both draggable AND operable from
 * the keyboard.
 *
 * ── Two sliders, not two divs with pointer handlers ─────────────────────────────────────────
 * Each is `role="slider"` with a real `aria-valuenow` and an `aria-valuetext` that says the colour
 * in words, and each answers the arrow keys. That is not decoration on top of a mouse control: the
 * mobile app's own picker is reachable only by dragging, and this app does not repeat that — every
 * drag here has a keyboard path, and the HEX field beside it is the third way in for anyone who
 * would rather type.
 *
 * The plane is TWO-dimensional, which `role="slider"` has no vocabulary for. It is exposed as the
 * saturation axis — the one the left/right keys move — and its `aria-valuetext` carries both
 * numbers plus the resulting hex, so a listener is never told half of what they changed. Up and
 * down move value; the pattern's own guidance for a 2D picker is exactly this.
 *
 * ── Pointer capture, and why the handler is on the element ──────────────────────────────────
 * `setPointerCapture` keeps the drag alive when the pointer leaves the box, which is what makes the
 * corners of the plane reachable at all — without it a drag towards pure white stops the moment the
 * cursor crosses the edge. Nothing is bound to `window`.
 *
 * ── The box is measured once, and moves are folded into a frame ─────────────────────────────
 * A captured drag cannot move the element it started on, so its rectangle is read on `pointerdown`
 * and reused: `getBoundingClientRect` per `pointermove` is a forced layout at the pointer's own
 * rate. The moves themselves are coalesced into one `requestAnimationFrame` — a mouse reports
 * faster than the screen repaints, and every extra report was a render nobody saw. The keyboard
 * path is untouched: an arrow press is one deliberate step and goes straight through.
 */

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { clamp01 } from '~/shared/lib/hex';

import { HUE_BAR_STOPS, hsvToHex, hueAtPosition, positionForHue, type Hsv } from '../model/colorPicker';
import styles from './ColorPicker.module.css';

interface ColorPickerProps {
  readonly hsv: Hsv;
  readonly onPlaneChange: (saturation: number, value: number) => void;
  readonly onHueChange: (hue: number) => void;
}

/** One arrow press. A hundredth of the plane, and one degree of the wheel. */
const PLANE_STEP = 0.01;
const PLANE_PAGE = 0.1;
const HUE_STEP = 1;
const HUE_PAGE = 15;

/** The box a drag started in, plus the frame its moves are being folded into. */
interface Drag {
  readonly box: DOMRect;
  /** `0` when no frame is booked — `requestAnimationFrame` never returns it. */
  frame: number;
  pending: (() => void) | null;
}

/** The percentage a knob sits at, as a string for the inline custom property. */
function percent(fraction: number): string {
  return `${String(clamp01(fraction) * 100)}%`;
}

export function ColorPicker({ hsv, onPlaneChange, onHueChange }: ColorPickerProps) {
  const { t } = useTranslation();
  const drag = useRef<Drag | null>(null);

  const hex = hsvToHex(hsv);
  const huePosition = positionForHue(hsv.h);

  const endDrag = () => {
    const current = drag.current;
    if (current !== null && current.frame !== 0) cancelAnimationFrame(current.frame);
    drag.current = null;
  };

  // A drag interrupted by an unmount must not leave a frame booked against a dead component.
  useEffect(() => endDrag, []);

  const startDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    event.currentTarget.focus();
    drag.current = {
      box: event.currentTarget.getBoundingClientRect(),
      frame: 0,
      pending: null,
    };
  };

  /** Keep only the last position seen inside a frame — the ones before it were never painted. */
  const onFrame = (run: (box: DOMRect) => void) => {
    const current = drag.current;
    if (current === null) return;
    current.pending = () => {
      run(current.box);
    };
    if (current.frame !== 0) return;
    current.frame = requestAnimationFrame(() => {
      current.frame = 0;
      const next = current.pending;
      current.pending = null;
      next?.();
    });
  };

  const readPlane = (clientX: number, clientY: number) => {
    onFrame((box) => {
      if (box.width === 0 || box.height === 0) return;
      onPlaneChange(
        clamp01((clientX - box.left) / box.width),
        clamp01(1 - (clientY - box.top) / box.height),
      );
    });
  };

  const readHue = (clientX: number) => {
    onFrame((box) => {
      if (box.width === 0) return;
      onHueChange(hueAtPosition((clientX - box.left) / box.width));
    });
  };

  /** Arrow, Home and End on the plane. Left/right is saturation, up/down is value. */
  const onPlaneKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? PLANE_PAGE : PLANE_STEP;
    const moves: Readonly<Record<string, [number, number]>> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowDown: [0, -step],
      ArrowUp: [0, step],
      PageDown: [0, -PLANE_PAGE],
      PageUp: [0, PLANE_PAGE],
    };
    const move = moves[event.key];
    if (move !== undefined) {
      event.preventDefault();
      onPlaneChange(clamp01(hsv.s + move[0]), clamp01(hsv.v + move[1]));
      return;
    }
    // The two corners a person actually asks for: the pure hue, and nothing at all.
    if (event.key === 'End') {
      event.preventDefault();
      onPlaneChange(1, 1);
    }
    if (event.key === 'Home') {
      event.preventDefault();
      onPlaneChange(0, 1);
    }
  };

  const onHueKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = event.key === 'PageUp' || event.key === 'PageDown' ? HUE_PAGE : HUE_STEP;
    const direction =
      event.key === 'ArrowLeft' || event.key === 'ArrowDown' || event.key === 'PageDown'
        ? -1
        : event.key === 'ArrowRight' || event.key === 'ArrowUp' || event.key === 'PageUp'
          ? 1
          : 0;
    if (direction !== 0) {
      event.preventDefault();
      // Wrapped, not clamped: the wheel has no ends, and stopping at red would make the shortest
      // route between two neighbouring hues a walk across the whole bar.
      onHueChange((hsv.h + direction * step + 360) % 360);
      return;
    }
    if (event.key === 'Home') {
      event.preventDefault();
      onHueChange(0);
    }
    if (event.key === 'End') {
      event.preventDefault();
      onHueChange(hueAtPosition(1));
    }
  };

  return (
    <div className={styles.picker}>
      <div
        className={styles.plane}
        role="slider"
        tabIndex={0}
        aria-label={t('profile.brandColor.planeA11y')}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(hsv.s * 100)}
        aria-valuetext={t('web:account.brandColor.planeValue', {
          saturation: Math.round(hsv.s * 100),
          value: Math.round(hsv.v * 100),
          hex: hex.toUpperCase(),
        })}
        style={{ '--picker-hue': String(hsv.h) } as React.CSSProperties}
        onKeyDown={onPlaneKeyDown}
        onPointerDown={(event) => {
          startDrag(event);
          readPlane(event.clientX, event.clientY);
        }}
        onPointerMove={(event) => {
          if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            readPlane(event.clientX, event.clientY);
          }
        }}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <span
          className={styles.knob}
          style={
            {
              '--knob-x': percent(hsv.s),
              '--knob-y': percent(1 - hsv.v),
              '--knob-fill': hex,
            } as React.CSSProperties
          }
        />
      </div>

      <div
        className={styles.hue}
        role="slider"
        tabIndex={0}
        aria-label={t('profile.brandColor.hueA11y')}
        aria-valuemin={0}
        aria-valuemax={360}
        aria-valuenow={Math.round(hsv.h)}
        aria-valuetext={t('web:account.brandColor.hueValue', { hue: Math.round(hsv.h) })}
        style={{ '--hue-stops': HUE_BAR_STOPS.join(', ') } as React.CSSProperties}
        onKeyDown={onHueKeyDown}
        onPointerDown={(event) => {
          startDrag(event);
          readHue(event.clientX);
        }}
        onPointerMove={(event) => {
          if (event.currentTarget.hasPointerCapture(event.pointerId)) readHue(event.clientX);
        }}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <span
          className={`${String(styles.knob)} ${String(styles.hueKnob)}`}
          style={
            {
              '--knob-x': percent(huePosition),
              '--knob-fill': hsvToHex({ h: hsv.h, s: 1, v: 1 }),
            } as React.CSSProperties
          }
        />
      </div>
    </div>
  );
}
